import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClientService } from "@/services/ClientService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    Loader2,
    UserPlus,
    Mail,
    Phone,
    MoreVertical,
    Pencil,
    Trash2,
    Briefcase
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ContactDialog } from "./contact-dialog";
import { ClientContact } from "@shared/schema";

interface ContactsTabProps {
    clientId: number;
}

export function ContactsTab({ clientId }: ContactsTabProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState<ClientContact | undefined>();

    const { data, isLoading } = useQuery({
        queryKey: ["client-contacts", clientId],
        queryFn: () => ClientService.getContacts(clientId),
    });

    const contacts = data?.data || [];

    const deleteMutation = useMutation({
        mutationFn: (id: number) => ClientService.deleteContact(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["client-contacts", clientId] });
            toast({
                title: "Contact deleted",
                description: "The contact has been deactivated.",
            });
        },
    });

    const handleEdit = (contact: ClientContact) => {
        setSelectedContact(contact);
        setIsDialogOpen(true);
    };

    const handleAdd = () => {
        setSelectedContact(undefined);
        setIsDialogOpen(true);
    };

    if (isLoading) {
        return (
            <div className="flex h-32 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div>
                        <CardTitle className="text-lg font-medium">Client Contacts</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            People who manage bookings or planning for this client.
                        </p>
                    </div>
                    <Button size="sm" onClick={handleAdd}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add Contact
                    </Button>
                </CardHeader>
                <CardContent>
                    {contacts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center opacity-50">
                            <Briefcase className="h-12 w-12 mb-4" />
                            <p>No contacts found for this client.</p>
                            <p className="text-sm">Click "Add Contact" to create one.</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Contact Info</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {contacts.map((contact) => (
                                    <TableRow key={contact.id}>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                                {contact.name}
                                                {contact.isPrimary && (
                                                    <Badge variant="default" className="text-[10px] h-4 px-1">Primary</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {contact.role || "-"}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1 text-sm">
                                                {contact.email && (
                                                    <div className="flex items-center gap-1.5 text-muted-foreground">
                                                        <Mail className="h-3 w-3" />
                                                        {contact.email}
                                                    </div>
                                                )}
                                                {contact.phone && (
                                                    <div className="flex items-center gap-1.5 text-muted-foreground">
                                                        <Phone className="h-3 w-3" />
                                                        {contact.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={contact.isActive ? "outline" : "secondary"}>
                                                {contact.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => handleEdit(contact)}>
                                                        <Pencil className="mr-2 h-4 w-4" />
                                                        Edit
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        className="text-destructive focus:text-destructive"
                                                        onClick={() => {
                                                            if (confirm("Are you sure you want to deactivate this contact?")) {
                                                                deleteMutation.mutate(contact.id);
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                        Deactivate
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <ContactDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                contact={selectedContact}
                clientId={clientId}
            />
        </div>
    );
}
